import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { tubeBore } from '../acoustics/geometry'
import type { Pt } from '../acoustics/polygon'
import type { Caps } from '../model/schema'

const SEGMENTS = 64

function ellipsePath<T extends THREE.Path>(path: T, rx: number, rz: number, clockwise: boolean): T {
  path.absellipse(0, 0, rx, rz, 0, Math.PI * 2, clockwise, 0)
  return path
}

/** Extrude an outline along +Z by `depth`, then stand it up so the extrusion runs along Y, centred on the origin. */
function standUp(shape: THREE.Shape, depth: number, yStart: number): THREE.BufferGeometry {
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: SEGMENTS, steps: 1 })
  g.rotateX(-Math.PI / 2) // (x, y, z) → (x, z, −y): depth along +Y
  g.translate(0, yStart, 0)
  return g
}

/**
 * A hollow cylinder: elliptical outer wall, a bore of `wall` less radius, optional end plates `wall` thick.
 * Dimensions are in metres and the mesh is NOT scaled afterwards, so the wall keeps its thickness when resized.
 */
export function buildTubeGeometry(size: [number, number, number], wall: number, caps: Caps): THREE.BufferGeometry {
  const half: [number, number, number] = [Math.abs(size[0]) / 2, Math.abs(size[1]) / 2, Math.abs(size[2]) / 2]
  const bore = tubeBore({ wall, caps }, half)
  const h = half[1] * 2
  const w = Math.min(Math.max(wall, 0.01), 0.98 * Math.min(half[0], half[2]))

  const ring = ellipsePath(new THREE.Shape(), half[0], half[2], false)
  ring.holes.push(ellipsePath(new THREE.Path(), bore.rx, bore.rz, true))
  const parts = [standUp(ring, h, -half[1])]
  const plate = () => ellipsePath(new THREE.Shape(), half[0], half[2], false)
  if (caps === 'top' || caps === 'both') parts.push(standUp(plate(), Math.min(w, h / 2), half[1] - Math.min(w, h / 2)))
  if (caps === 'bottom' || caps === 'both') parts.push(standUp(plate(), Math.min(w, h / 2), -half[1]))
  const merged = mergeGeometries(parts.map((p) => (p.index ? p.toNonIndexed() : p)))
  for (const p of parts) p.dispose()
  return merged
}

/** Flat disc (or ring when `core` > 0) lying in the XY plane, with UVs in metres. */
export function discGeometry(rx: number, rz: number, core: number): THREE.ShapeGeometry {
  const outline = ellipsePath(new THREE.Shape(), rx, rz, false)
  if (core > 0) outline.holes.push(ellipsePath(new THREE.Path(), core, core, true))
  return new THREE.ShapeGeometry(outline, SEGMENTS)
}

/** Line segments tracing an ellipse at height y (for room outlines). */
export function ellipseSegments(rx: number, rz: number, y: number, n = 128): number[] {
  const pts: number[] = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    const b = ((i + 1) / n) * Math.PI * 2
    pts.push(rx * Math.cos(a), y, rz * Math.sin(a), rx * Math.cos(b), y, rz * Math.sin(b))
  }
  return pts
}

/** Spans of the line {coordinate = c} (running along the other axis) that lie inside an elliptical room, outside its core. */
export function gridSpans(c: number, along: 'z' | 'x', rx: number, rz: number, core: number): [number, number][] {
  // For lines of constant x (running along z) the room's half-extent along z is rz·√(1 − (c/rx)²); swapped for constant z.
  const [rc, rOther] = along === 'z' ? [rx, rz] : [rz, rx]
  const k = 1 - (c / rc) ** 2
  if (k <= 0) return []
  const half = rOther * Math.sqrt(k)
  if (core > 0 && Math.abs(c) < core) {
    const cz = Math.sqrt(core * core - c * c)
    return cz >= half
      ? []
      : [
          [-half, -cz],
          [cz, half],
        ]
  }
  return [[-half, half]]
}

/** Shape in the XY plane from (x, z) points: `flipZ` maps z → −y, which a −90° turn about X (floors) maps back onto +z. */
function shapeFromPoints(points: Pt[], flipZ: boolean): THREE.Shape {
  const s = new THREE.Shape()
  points.forEach(([x, z], i) => {
    if (i === 0) s.moveTo(x, flipZ ? -z : z)
    else s.lineTo(x, flipZ ? -z : z)
  })
  s.closePath()
  return s
}

/**
 * An extruded footprint with unit height, centred on the origin, in unit coordinates (the mesh is scaled by the
 * object's size afterwards). Holes are extra contours.
 */
export function prismGeometry(outline: Pt[], holes: Pt[][]): THREE.BufferGeometry {
  const shape = shapeFromPoints(outline, true)
  for (const h of holes) {
    const path = new THREE.Path()
    h.forEach(([x, z], i) => {
      if (i === 0) path.moveTo(x, -z)
      else path.lineTo(x, -z)
    })
    path.closePath()
    shape.holes.push(path)
  }
  return standUp(shape, 1, -0.5)
}

/** Flat polygon for a custom room's floor (`ceiling` = false) or ceiling, in the XY plane; rotate it onto the horizontal. */
export function polygonGeometry(points: Pt[], ceiling: boolean): THREE.ShapeGeometry {
  return new THREE.ShapeGeometry(shapeFromPoints(points, !ceiling))
}

/**
 * Inward-facing walls for a counter-clockwise outline: one quad per edge. UVs are in "tiles" (metres / tile),
 * so the concrete texture runs along the walls without stretching.
 */
export function wallsGeometry(c: Pt[], height: number, tile: number): THREE.BufferGeometry {
  const pos: number[] = []
  const uv: number[] = []
  const idx: number[] = []
  let run = 0
  c.forEach((a, i) => {
    const b = c[(i + 1) % c.length]!
    const l = Math.hypot(b[0] - a[0], b[1] - a[1])
    const base = pos.length / 3
    pos.push(a[0], 0, a[1], b[0], 0, b[1], b[0], height, b[1], a[0], height, a[1])
    uv.push(run / tile, 0, (run + l) / tile, 0, (run + l) / tile, height / tile, run / tile, height / tile)
    // Counter-clockwise outline: the interior is on the left, and (a→b) × up points left, so this winding faces inward.
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3)
    run += l
  })
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
  g.setIndex(idx)
  g.computeVertexNormals()
  return g
}

/** Line segments for a polygon prism outline: floor loop, ceiling loop and a vertical at every corner. */
export function outlineSegments(c: Pt[], height: number): number[] {
  const pts: number[] = []
  c.forEach((a, i) => {
    const b = c[(i + 1) % c.length]!
    pts.push(a[0], 0, a[1], b[0], 0, b[1], a[0], height, a[1], b[0], height, b[1], a[0], 0, a[1], a[0], height, a[1])
  })
  return pts
}
